import type { ComponentProps } from 'react';

import { Skeleton, Stack } from '../../../components';
import { Box } from '../../../components/box';
import { Divider } from '../../../components/divider';
import { List, ListItem, ListItemIcon } from '../../../components/list';
import { useFenrirTranslation } from '../../../locales/use-fenrir-translation';

import { CollapsibleMenu, type CollapsableMenuItem } from './collapsible-menu';
import { RouteMenu, type RouteMenuItem } from './route-menu';

export type MenuItem = RouteMenuItem | CollapsableMenuItem;

export type MenuItemSkeletonCount = number;

const isCollapsableMenuItem = (item: MenuItem): item is CollapsableMenuItem =>
  'children' in item;

type NavigationMenuItemsProps = {
  items: MenuItem[];
  isLoading?: boolean;
  skeletonItemSizes: string[];
};

const NavigationListSkeleton = ({
  skeletonItemSizes,
}: {
  skeletonItemSizes: string[];
}) => {
  const { t } = useFenrirTranslation({ keyPrefix: 'common' });

  return (
    <>
      <span aria-label={t('loading')} />
      <List aria-hidden="true">
        {skeletonItemSizes.map((textWidth, index) => (
          <ListItem key={index}>
            <ListItemIcon>
              <Skeleton variant="circular" width={22} height={22} />
            </ListItemIcon>

            <Skeleton
              variant="rectangular"
              height={22}
              width={textWidth}
              // match exact spacing to match real component
              sx={{ marginY: '5px' }}
            />
          </ListItem>
        ))}
      </List>
    </>
  );
};

const NavigationSection = ({
  items,
  isLoading,
  skeletonItemSizes,
  ...rest
}: NavigationMenuItemsProps & ComponentProps<'nav'>) => {
  return (
    <nav {...rest} aria-busy={isLoading}>
      {isLoading ? (
        <NavigationListSkeleton skeletonItemSizes={skeletonItemSizes} />
      ) : (
        <List>
          {items.map((item) => {
            if (isCollapsableMenuItem(item) && !item.children.length)
              return null;

            return isCollapsableMenuItem(item) ? (
              <CollapsibleMenu key={item.title} item={item} />
            ) : (
              <RouteMenu key={item.title} item={item} />
            );
          })}
        </List>
      )}
    </nav>
  );
};

type NavigationMenuProps = {
  primaryMenu: MenuItem[];
  secondaryMenu?: MenuItem[];
  isLoading?: boolean;
};

const primarySkeletonItemSizes = ['20%', '65%', '45%', '80%', '55%', '45%'];
const secondarySkeletonItemSizes = ['55%', '65%', '45%'];

// TODO: @evagm: add snapshot for loading state
/** @experimental In active development. Avoid using until further notice. */
export const NavigationMenu = ({
  primaryMenu,
  secondaryMenu,
  isLoading = false,
}: NavigationMenuProps) => {
  const { t } = useFenrirTranslation({ keyPrefix: 'dashboardLayout' });

  return (
    <Stack sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
      <NavigationSection
        items={primaryMenu}
        aria-label={t('navigation.primary')}
        isLoading={isLoading}
        skeletonItemSizes={primarySkeletonItemSizes}
      />
      {secondaryMenu && (
        <>
          <Box sx={{ flex: 1 }} />
          <Divider />
          <NavigationSection
            items={secondaryMenu}
            aria-label={t('navigation.secondary')}
            isLoading={isLoading}
            skeletonItemSizes={secondarySkeletonItemSizes}
          />
        </>
      )}
    </Stack>
  );
};
